import { debugLogger } from '@/utils/debugLogger';

interface ConversionCache {
  originalUrl: string;
  convertedBlob: Blob;
  convertedUrl: string;
  timestamp: number;
  format: string;
}

class RealtimeAudioConverter {
  private static instance: RealtimeAudioConverter;
  private cache = new Map<string, ConversionCache>();
  private audioContext: AudioContext | null = null;
  private isIOS: boolean;
  private isSafari: boolean;
  private conversionAttempts = new Map<string, number>();
  private readonly MAX_CONVERSION_ATTEMPTS = 3;

  private constructor() {
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    debugLogger.debug('RealtimeAudioConverter', 'Initialized:', {
      isIOS: this.isIOS,
      isSafari: this.isSafari,
      userAgent: navigator.userAgent.substring(0, 100)
    });
  }

  static getInstance(): RealtimeAudioConverter {
    if (!RealtimeAudioConverter.instance) {
      RealtimeAudioConverter.instance = new RealtimeAudioConverter();
    }
    return RealtimeAudioConverter.instance;
  }

  // Check if platform needs conversion
  needsConversion(): boolean {
    return this.isIOS || this.isSafari;
  }

  // Get or create AudioContext with iOS optimizations
  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioContextClass();
      
      // Resume context on iOS if suspended
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }
    }
    return this.audioContext;
  }

  // Validate and fix URL before download
  private validateAndFixUrl(url: string): string | null {
    try {
      // Check if URL is complete and not truncated
      if (!url || url.length < 50) {
        debugLogger.error('RealtimeAudioConverter', 'URL too short or empty:', url?.substring(0, 100));
        return null;
      }

      // Check if URL contains required token
      if (!url.includes('token=')) {
        debugLogger.error('RealtimeAudioConverter', 'URL missing token parameter:', url.substring(0, 100));
        return null;
      }

      // Validate URL format
      const urlObj = new URL(url);
      if (!urlObj.hostname || !urlObj.pathname) {
        debugLogger.error('RealtimeAudioConverter', 'Invalid URL format:', url.substring(0, 100));
        return null;
      }

      debugLogger.debug('RealtimeAudioConverter', 'URL validation passed:', url.substring(0, 100));
      return url;
    } catch (error) {
      debugLogger.error('RealtimeAudioConverter', 'URL validation failed:', error);
      return null;
    }
  }

  // Enhanced audio download with retry and validation
  private async downloadAudio(url: string): Promise<ArrayBuffer> {
    const validUrl = this.validateAndFixUrl(url);
    if (!validUrl) {
      throw new Error('Invalid or truncated URL');
    }

    debugLogger.debug('RealtimeAudioConverter', 'Starting download:', validUrl.substring(0, 100));
    
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        debugLogger.debug('RealtimeAudioConverter', `Download attempt ${attempt}/${maxRetries}`);
        
        const response = await fetch(validUrl, {
          mode: 'cors',
          cache: 'no-cache', // Avoid cached corrupted data
          headers: {
            'Accept': 'audio/*',
            'Range': 'bytes=0-' // Ensure we get the full file
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const contentLength = response.headers.get('content-length');
        debugLogger.debug('RealtimeAudioConverter', 'Download headers:', {
          contentType: response.headers.get('content-type'),
          contentLength,
          status: response.status
        });

        const arrayBuffer = await response.arrayBuffer();
        
        // Validate downloaded data
        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          throw new Error('Downloaded file is empty');
        }

        if (contentLength && parseInt(contentLength) !== arrayBuffer.byteLength) {
          throw new Error(`File size mismatch: expected ${contentLength}, got ${arrayBuffer.byteLength}`);
        }

        debugLogger.debug('RealtimeAudioConverter', 'Download successful:', {
          size: arrayBuffer.byteLength,
          attempt
        });

        return arrayBuffer;
      } catch (error) {
        lastError = error as Error;
        debugLogger.warn('RealtimeAudioConverter', `Download attempt ${attempt} failed:`, error);
        
        if (attempt < maxRetries) {
          const delay = attempt * 1000; // Progressive delay
          debugLogger.debug('RealtimeAudioConverter', `Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    throw new Error(`Download failed after ${maxRetries} attempts: ${lastError?.message}`);
  }

  // Enhanced audio buffer conversion
  private async convertAudioBuffer(audioBuffer: AudioBuffer): Promise<Blob> {
    debugLogger.debug('RealtimeAudioConverter', 'Converting audio buffer:', {
      duration: audioBuffer.duration,
      sampleRate: audioBuffer.sampleRate,
      channels: audioBuffer.numberOfChannels
    });

    // Validate audio buffer
    if (!audioBuffer || audioBuffer.duration <= 0) {
      throw new Error('Invalid audio buffer');
    }

    // For iOS, create optimized WAV format
    if (this.isIOS) {
      return this.createOptimizedWAV(audioBuffer);
    }

    // For Safari on Mac, create MP4-compatible format
    return this.createMP4Compatible(audioBuffer);
  }

  // Create optimized WAV for iOS with better error handling
  private createOptimizedWAV(audioBuffer: AudioBuffer): Blob {
    try {
      const numberOfChannels = Math.min(audioBuffer.numberOfChannels, 2); // Max 2 channels for compatibility
      const sampleRate = audioBuffer.sampleRate;
      const length = audioBuffer.length * numberOfChannels * 2; // 16-bit samples
      
      const buffer = new ArrayBuffer(44 + length);
      const view = new DataView(buffer);
      
      // WAV header with iOS optimizations
      const writeString = (offset: number, string: string) => {
        for (let i = 0; i < string.length; i++) {
          view.setUint8(offset + i, string.charCodeAt(i));
        }
      };
      
      writeString(0, 'RIFF');
      view.setUint32(4, 36 + length, true);
      writeString(8, 'WAVE');
      writeString(12, 'fmt ');
      view.setUint32(16, 16, true);
      view.setUint16(20, 1, true); // PCM format
      view.setUint16(22, numberOfChannels, true);
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, sampleRate * numberOfChannels * 2, true);
      view.setUint16(32, numberOfChannels * 2, true);
      view.setUint16(34, 16, true); // 16-bit
      writeString(36, 'data');
      view.setUint32(40, length, true);
      
      // Convert audio data to 16-bit PCM with error handling
      let offset = 44;
      for (let i = 0; i < audioBuffer.length; i++) {
        for (let channel = 0; channel < numberOfChannels; channel++) {
          const channelData = audioBuffer.getChannelData(Math.min(channel, audioBuffer.numberOfChannels - 1));
          const sample = Math.max(-1, Math.min(1, channelData[i] || 0));
          const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
          view.setInt16(offset, intSample, true);
          offset += 2;
        }
      }
      
      debugLogger.debug('RealtimeAudioConverter', 'WAV creation successful:', {
        originalChannels: audioBuffer.numberOfChannels,
        outputChannels: numberOfChannels,
        sampleRate,
        size: buffer.byteLength
      });
      
      return new Blob([buffer], { type: 'audio/wav' });
    } catch (error) {
      debugLogger.error('RealtimeAudioConverter', 'WAV creation failed:', error);
      throw new Error(`WAV conversion failed: ${error}`);
    }
  }

  // Create MP4-compatible format for Safari
  private createMP4Compatible(audioBuffer: AudioBuffer): Blob {
    // For Safari, create WAV but mark as MP4 for compatibility
    const wavBlob = this.createOptimizedWAV(audioBuffer);
    return new Blob([wavBlob], { type: 'audio/mp4' });
  }

  // Check conversion attempts to prevent infinite loops
  private canAttemptConversion(url: string): boolean {
    const attempts = this.conversionAttempts.get(url) || 0;
    return attempts < this.MAX_CONVERSION_ATTEMPTS;
  }

  private incrementConversionAttempts(url: string): void {
    const attempts = (this.conversionAttempts.get(url) || 0) + 1;
    this.conversionAttempts.set(url, attempts);
    
    // Clean up old attempts (older than 5 minutes)
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    for (const [key, _] of this.conversionAttempts.entries()) {
      const cached = this.cache.get(key);
      if (!cached || cached.timestamp < fiveMinutesAgo) {
        this.conversionAttempts.delete(key);
      }
    }
  }

  // Main conversion method with improved error handling
  async convertAudio(originalUrl: string, onProgress?: (progress: number) => void): Promise<string> {
    try {
      // Check if we can attempt conversion
      if (!this.canAttemptConversion(originalUrl)) {
        throw new Error(`Maximum conversion attempts reached for this audio (${this.MAX_CONVERSION_ATTEMPTS})`);
      }

      // Check cache first
      const cached = this.cache.get(originalUrl);
      if (cached && this.isCacheValid(cached)) {
        debugLogger.debug('RealtimeAudioConverter', 'Using cached conversion:', originalUrl.substring(0, 100));
        return cached.convertedUrl;
      }

      debugLogger.debug('RealtimeAudioConverter', 'Starting conversion:', originalUrl.substring(0, 100));
      this.incrementConversionAttempts(originalUrl);
      
      onProgress?.(10); // Starting download
      
      // Download audio data with enhanced error handling
      const arrayBuffer = await this.downloadAudio(originalUrl);
      onProgress?.(40); // Download complete
      
      // Get audio context and decode
      const audioContext = this.getAudioContext();
      
      try {
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0)); // Clone buffer
        onProgress?.(70); // Decode complete
        
        // Convert to compatible format
        const convertedBlob = await this.convertAudioBuffer(audioBuffer);
        onProgress?.(90); // Conversion complete
        
        // Create object URL
        const convertedUrl = URL.createObjectURL(convertedBlob);
        
        // Cache the result
        const cacheEntry: ConversionCache = {
          originalUrl,
          convertedBlob,
          convertedUrl,
          timestamp: Date.now(),
          format: convertedBlob.type
        };
        
        this.cache.set(originalUrl, cacheEntry);
        
        // Clean old cache entries (keep last 10)
        this.cleanCache();
        
        onProgress?.(100); // Complete
        
        debugLogger.debug('RealtimeAudioConverter', 'Conversion completed successfully:', {
          originalSize: arrayBuffer.byteLength,
          convertedSize: convertedBlob.size,
          format: convertedBlob.type,
          duration: audioBuffer.duration
        });
        
        return convertedUrl;
        
      } catch (decodeError) {
        debugLogger.error('RealtimeAudioConverter', 'Audio decoding failed:', decodeError);
        throw new Error(`Audio decoding failed: ${decodeError}`);
      }
      
    } catch (error) {
      debugLogger.error('RealtimeAudioConverter', 'Conversion failed:', error);
      throw new Error(`Audio conversion failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Enhanced validation with better error detection
  async validateAudioPlayback(url: string): Promise<boolean> {
    const validUrl = this.validateAndFixUrl(url);
    if (!validUrl) {
      return false;
    }

    return new Promise((resolve) => {
      const audio = new Audio();
      const timeout = setTimeout(() => {
        cleanup();
        resolve(false);
      }, 5000); // Increased timeout
      
      const cleanup = () => {
        clearTimeout(timeout);
        audio.removeEventListener('loadedmetadata', onLoad);
        audio.removeEventListener('error', onError);
        audio.removeEventListener('canplaythrough', onCanPlay);
        audio.src = '';
      };
      
      const onLoad = () => {
        const isValid = audio.duration > 0 && !isNaN(audio.duration) && isFinite(audio.duration);
        debugLogger.debug('RealtimeAudioConverter', 'Audio validation (loadedmetadata):', {
          url: validUrl.substring(0, 100),
          duration: audio.duration,
          readyState: audio.readyState,
          isValid
        });
        
        if (isValid) {
          cleanup();
          resolve(true);
        }
      };
      
      const onCanPlay = () => {
        cleanup();
        debugLogger.debug('RealtimeAudioConverter', 'Audio validation (canplaythrough) success');
        resolve(true);
      };
      
      const onError = (event: any) => {
        cleanup();
        debugLogger.debug('RealtimeAudioConverter', 'Audio validation failed:', {
          url: validUrl.substring(0, 100),
          error: event?.target?.error || 'Unknown error'
        });
        resolve(false);
      };
      
      audio.addEventListener('loadedmetadata', onLoad);
      audio.addEventListener('canplaythrough', onCanPlay);
      audio.addEventListener('error', onError);
      audio.crossOrigin = 'anonymous';
      audio.preload = 'metadata';
      audio.src = validUrl;
      audio.load();
    });
  }

  private isCacheValid(cache: ConversionCache): boolean {
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    return (Date.now() - cache.timestamp) < maxAge;
  }

  private cleanCache(): void {
    const entries = Array.from(this.cache.entries());
    
    // Remove expired entries
    entries.forEach(([key, cache]) => {
      if (!this.isCacheValid(cache)) {
        URL.revokeObjectURL(cache.convertedUrl);
        this.cache.delete(key);
      }
    });
    
    // Keep only last 10 entries
    if (this.cache.size > 10) {
      const sortedEntries = entries
        .filter(([, cache]) => this.isCacheValid(cache))
        .sort((a, b) => b[1].timestamp - a[1].timestamp);
      
      // Remove oldest entries
      const toKeep = sortedEntries.slice(0, 10);
      const toRemove = sortedEntries.slice(10);
      
      toRemove.forEach(([key, cache]) => {
        URL.revokeObjectURL(cache.convertedUrl);
        this.cache.delete(key);
      });
    }
  }

  clearCache(): void {
    this.cache.forEach(cache => {
      URL.revokeObjectURL(cache.convertedUrl);
    });
    this.cache.clear();
    this.conversionAttempts.clear();
    debugLogger.debug('RealtimeAudioConverter', 'Cache cleared');
  }

  getSupportedFormats(): string[] {
    if (this.isIOS) {
      return ['audio/wav', 'audio/mp4', 'audio/mpeg'];
    }
    if (this.isSafari) {
      return ['audio/mp4', 'audio/wav', 'audio/mpeg'];
    }
    return ['audio/webm', 'audio/mp4', 'audio/wav', 'audio/ogg'];
  }

  destroy(): void {
    this.clearCache();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

export const realtimeAudioConverter = RealtimeAudioConverter.getInstance();

// Cleanup on page unload (disabled during tests)
if (!(typeof import.meta !== 'undefined' && (import.meta as any).env?.VITEST)) {
  window.addEventListener('beforeunload', () => {
    realtimeAudioConverter.destroy();
  });
}
